import { Header, List, ProductPickModal, Text } from "@src/components"
import { MenuProductEditModal } from "@src/components/menu-product-edit-modal"
import MIcon from "@react-native-vector-icons/material-design-icons"
import { useNumCols } from "@src/hooks"
import { ENV, priceFromDBFormat, sharedStyles, trpc, type RouterOutputs } from "@src/utils"
import { Image } from "expo-image"
import { Link, useLocalSearchParams } from "expo-router"
import { ActivityIndicator, Pressable, View } from "react-native"
import { ar } from "@src/i18n/ar"
import { SafeAreaView } from "react-native-safe-area-context"
import { Show, useObservable } from "@legendapp/state/react"
import { useState } from "react"

type ProductItem = RouterOutputs["menu"]["collection"]["products"][number]

export default function Collection() {
  const params = useLocalSearchParams<{ id: string }>()
  const numCols = Math.min(4, Math.max(2, useNumCols(250, { maxWidth: 1400 })))
  const id = Number.parseInt(params.id, 10)
  const collection = trpc.menu.collection.useQuery({ id })
  const [editingProduct, setEditingProduct] = useState<ProductItem | null>(null)

  const header = (
    <Header
      title={collection.data?.name ?? ""}
      right={
        collection.isLoading ? null : (
          <View className="flex-row h-full">
            <LinkProductsToCollection id={id} />
            <Pressable onPress={() => collection.refetch()} className="px-2 h-full justify-center items-center">
              <MIcon name="refresh" size={24} color="#3B82F6" />
            </Pressable>
          </View>
        )
      }
    />
  )

  if (collection.isLoading) {
    return (
      <>
        {header}
        <ActivityIndicator className="mt-4" />
      </>
    )
  }

  return (
    <>
      {header}
      <List
        contentContainerStyle={sharedStyles.contentContainer}
        data={collection.data?.products ?? []}
        numColumns={numCols}
        key={`storage-products-${numCols}`}
        keyExtractor={(item) => item.id.toString()}
        onRefresh={collection.refetch}
        onEndReachedThreshold={0.8}
        ListEmptyComponent={<Text className="text-gray-500 text-center py-8">No products.</Text>}
        renderItem={({ item }) => <ProductCard item={item} onEdit={() => setEditingProduct(item)} />}
        ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
      />

      {/* Edit Modal */}
      {editingProduct && (
        <MenuProductEditModal
          productId={editingProduct.id}
          initialValues={{
            listedInApp: Boolean(editingProduct.listedInApp),
            isAvailable: Boolean(editingProduct.isAvailable),
            canExceedInventory: Boolean(editingProduct.canExceedInventory),
            maxOrderQuantity: editingProduct.maxOrderQuantity,
            price: editingProduct.price,
            originalPrice: editingProduct.originalPrice,
            name: editingProduct.name,
          }}
          onClose={() => setEditingProduct(null)}
          onSuccess={() => collection.refetch()}
        />
      )}
    </>
  )
}

function ProductCard({ item, onEdit }: { item: ProductItem; onEdit: () => void }) {
  const image = item.images?.at(-1)
  const isHidden = !item.listedInApp
  const isArchived = item.status === "inactive"
  const isUnavailable = !item.isAvailable

  // Calculate inventory quantity
  const inventoryQty = (() => {
    if (item.baseProduct?.inventory?.quantity != null && item.baseProduct.value && item.value) {
      // Derived product: calculate from base
      return Math.floor((item.baseProduct.inventory.quantity * item.baseProduct.value) / item.value)
    }
    return item.inventory?.quantity ?? 0
  })()

  return (
    <View
      className={`bg-white p-3 rounded mb-3 shadow-sm mx-1 flex-1 hover:shadow-xl transition-all border ${
        isArchived ? "border-red-200 opacity-60" : isHidden ? "border-orange-200 opacity-80" : "border-gray-100"
      }`}
    >
      {/* Edit Button */}
      <Pressable
        onPress={onEdit}
        className="absolute top-2 end-2 p-1.5 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full shadow-sm z-10"
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      >
        <MIcon name="pencil" size={16} color="#3B82F6" />
      </Pressable>

      {/* Status Badges */}
      <View className="absolute top-2 start-2 gap-1 z-10">
        {isArchived && (
          <View className="bg-red-500 px-2 py-0.5 rounded">
            <Text className="text-white text-xs font-bold">{ar.archived}</Text>
          </View>
        )}
        {isHidden && !isArchived && (
          <View className="bg-orange-500 px-2 py-0.5 rounded">
            <Text className="text-white text-xs font-bold">{ar.hidden}</Text>
          </View>
        )}
        {isUnavailable && !isArchived && (
          <View className="bg-gray-500 px-2 py-0.5 rounded">
            <Text className="text-white text-xs font-bold">{ar.unavailableForSale}</Text>
          </View>
        )}
      </View>

      <Link href={`/dashboard/products/${item.id}`} asChild>
        <Pressable className="active:opacity-70">
          {image ? (
            <Image
              contentFit="contain"
              cachePolicy="memory"
              source={`${ENV.R2_URL}/${image.key}`}
              style={{ width: "100%", height: 120, alignSelf: "center" }}
              recyclingKey={image.key}
              transition={250}
              loading="lazy"
            />
          ) : (
            <View className="w-full h-28 bg-gray-100 rounded items-center justify-center">
              <MIcon name="image" size={40} color="#9CA3AF" />
            </View>
          )}

          <View className="mt-3">
            <Text className="text-sm font-semibold text-gray-900 text-center leading-tight" numberOfLines={2} style={{ minHeight: 36 }}>
              {item.name}
            </Text>

            <View className="mt-3 items-center">
              <Text className="text-base md:text-lg font-bold text-green-600">
                {priceFromDBFormat(item.price)} {ar.egp}
              </Text>
              {item.originalPrice && item.originalPrice > item.price ? (
                <Text className="text-xs md:text-sm text-gray-500 line-through mt-0.5">
                  {priceFromDBFormat(item.originalPrice)} {ar.egp}
                </Text>
              ) : null}
            </View>
          </View>
        </Pressable>
      </Link>

      {/* Bottom Indicators */}
      <View className="flex-row items-center justify-between mt-3 pt-2 border-t border-gray-100">
        {/* Inventory */}
        <View className="flex-row items-center gap-1">
          <MIcon name="package-variant" size={14} color={inventoryQty > 0 ? "#16A34A" : "#DC2626"} />
          <Text className={`text-xs ${inventoryQty > 0 ? "text-green-600" : "text-red-600"}`}>{inventoryQty}</Text>
        </View>

        {/* Listed in App indicator */}
        <View className="flex-row items-center gap-1">
          <MIcon name={item.listedInApp ? "eye" : "eye-off"} size={14} color={item.listedInApp ? "#16A34A" : "#9CA3AF"} />
        </View>

        {/* Max Quantity */}
        {item.maxOrderQuantity && (
          <View className="flex-row items-center gap-1">
            <Text className="text-xs text-gray-500">Max: {item.maxOrderQuantity}</Text>
          </View>
        )}
      </View>
    </View>
  )
}

function LinkProductsToCollection({ id }: { id: number }) {
  const showProductsPicker$ = useObservable<boolean>(false)
  const utils = trpc.useUtils()
  const collection = trpc.menu.collection.useQuery({ id })
  const linkProductsToCollection = trpc.menu.linkProductsToCollection.useMutation()
  const productIds = collection.data?.products.map((p) => p.id) ?? []

  return (
    <>
      <Pressable onPress={() => showProductsPicker$.set(true)} className="px-2 h-full justify-center active:opacity-70">
        <MIcon name="link" size={24} />
      </Pressable>
      <Show if={showProductsPicker$}>
        {() => (
          <ProductPickModal
            value={productIds}
            types={["LOCAL_PRODUCT", "STANDARD_PRODUCT", "WEIGHT_DEFINED_PRODUCT"]}
            onChange={(ids) =>
              linkProductsToCollection.mutate(
                { collectionId: id, productIds: ids },
                {
                  onSuccess() {
                    showProductsPicker$.set(false)
                    collection.refetch()
                    utils.menu.get.invalidate()
                  },
                },
              )
            }
            hide={() => showProductsPicker$.set(false)}
          />
        )}
      </Show>
    </>
  )
}
